import { ReactNode, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Tooltip } from 'antd';

import { AppDispatch } from 'app/store';

import useApplicationActions from 'components/Applications/hooks/useApplicationActions';
import useJobActions from 'components/Jobseekers/hooks/useJobActions';
import useJobseekerActions from 'components/Jobseekers/hooks/useJobseekerActions';

import { closeSidebarForAction } from 'features/sidebar/sidebarSlice';

import { GTMEventLogger } from 'utils/analytics';
import { delay } from 'utils/delay';

import { SelectCardWrapperPermissionsDetails } from 'types/commons';

import { JOIN_HOST } from 'constants/endpoints';
import {
  CardActions,
  GTMEvents,
  SelectCardWrapperPermissions,
  SidebarContentTypes
} from 'constants/enums';
import { EDIT, JOBS } from 'constants/path';

import CardActionLabel from '../CardActionLabel';

interface CardAction {
  action: CardActions;
  onClick: () => void;
  permission?: SelectCardWrapperPermissions;
  event?: GTMEvents;
}

export const useCardActions = (
  sidebarContentType: SidebarContentTypes,
  permissions: SelectCardWrapperPermissionsDetails[] | undefined,
  contentId: string | undefined
) => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const [activeComponent, setActiveComponent] = useState<ReactNode | null>(
    null
  );

  const { sendOfferLetter } = useApplicationActions({
    applicationId: contentId || ''
  });

  const { inviteJobseekerToJob } = useJobseekerActions({
    jobseekerId: contentId || '',
    setActiveComponent
  });

  const { unsubscribeFromJob, reactivateJob } = useJobActions({
    jobId: contentId || '',
    setActiveComponent
  });

  // TODO: refactor this. we don't need to close sidebar on any action

  // const onClickAnyAction = () => {
  //   dispatch(closeSidebarKeepingSelectedItem());
  // };

  const base = [
    // {
    //   sidebarContentType: SidebarContentTypes.JobseekerView,
    //   actions: [
    //     {
    //       label: 'Action',
    //       onClick: () => console.log('Bulk Action')
    //     }
    //   ] as CardAction[]
    // },
    {
      sidebarContentType: SidebarContentTypes.ApplicationView,
      actions: [
        // Disabled byt the request in https://joblio.atlassian.net/browse/JJ-3164
        // {
        //   action: CardActions.ApplicationAccept,
        //   onClick: () => console.log('Accept'),
        //   permission: SelectCardWrapperPermissions.ApplicationAccept,
        //   event: GTMEvents.ApplicationsListMenuAccept
        // },
        // {
        //   action: CardActions.ApplicationSendOffer,
        //   onClick: sendOfferLetter,
        //   permission: SelectCardWrapperPermissions.ApplicationSendOffer,
        //   event: GTMEvents.ApplicationsListMenuOffer
        // },
        // {
        //   action: CardActions.ApplicationScheduleInterview,
        //   onClick: () => console.log('Schedule Interview'),
        //   permission: SelectCardWrapperPermissions.ApplicationScheduleInterview,
        //   event: GTMEvents.ApplicationsListMenuInterview
        // },
        // {
        //   action: CardActions.ApplicationReject,
        //   onClick: () => console.log('Reject'),
        //   permission: SelectCardWrapperPermissions.ApplicationReject,
        //   event: GTMEvents.ApplicationsListMenuReject
        // }
      ] as CardAction[]
    },
    {
      sidebarContentType: SidebarContentTypes.JobView,
      actions: [
        // {
        //   action: CardActions.JobPay,
        //   onClick: () => console.log('pay job'),
        //   permission: SelectCardWrapperPermissions.JobPay
        // },
        // {
        //   action: CardActions.JobUpgrade,
        //   onClick: () => console.log('upgrade job'),
        //   permission: SelectCardWrapperPermissions.JobUpgrade
        // },
        // {
        //   action: CardActions.JobEdit,
        //   onClick: async () => {
        //     dispatch(closeSidebarForAction());
        //     await delay(0);
        //     navigate(`${JOBS}/${contentId}${EDIT}`);
        //   },
        //   permission: SelectCardWrapperPermissions.JobEdit
        // },
        {
          action: CardActions.JobView,
          onClick: () => {
            const jobUrl = `${JOIN_HOST}/jobs/${contentId}`;
            window.open(jobUrl, '_blank');
          },
          permission: SelectCardWrapperPermissions.JobView
        },
        // {
        //   action: CardActions.JobDelete,
        //   onClick: () => console.log('delete job'),
        //   permission: SelectCardWrapperPermissions.JobDelete
        // },
        {
          action: CardActions.JobCancelSubscription,
          onClick: () => {
            unsubscribeFromJob();
            GTMEventLogger(GTMEvents.SubscriptionCanceled);
          },
          permission: SelectCardWrapperPermissions.JobCancelSubscription
        },
        {
          action: CardActions.JobReactivate,
          onClick: () => {
            reactivateJob();
            GTMEventLogger(GTMEvents.JobReactivate);
          },
          permission: SelectCardWrapperPermissions.JobReactivate
        }
      ] as CardAction[]
    },
    {
      sidebarContentType: SidebarContentTypes.JobseekerView,
      actions: [
        {
          action: CardActions.JobseekerInviteToJob,
          onClick: inviteJobseekerToJob,
          permission: SelectCardWrapperPermissions.JobseekerInviteToJob,
          event: GTMEvents.JobseekersListInvite
        }
      ] as CardAction[]
    }
  ];

  const match = base.find((el) => el.sidebarContentType === sidebarContentType);

  const actions =
    match?.actions
      .map((action, i) => {
        const permissionDetails = permissions?.find(
          (el) => el.permission === action.permission
        );

        if (permissionDetails?.isHidden || !permissionDetails) {
          return null;
        }

        const tooltip = permissionDetails?.isAllowed
          ? permissionDetails?.allowedTooltip
          : permissionDetails?.notAllowedTooltip;

        const isDisabled = !permissionDetails?.isAllowed;

        return {
          ...action,
          key: i,
          disabled: isDisabled,
          onClick: () => {
            // onClickAnyAction();
            action.onClick();
            if (action.event) {
              GTMEventLogger(action.event);
            }
          },
          label: (
            <Tooltip placement="left" trigger="hover" title={tooltip}>
              <>
                <CardActionLabel
                  action={action.action}
                  isDisabled={isDisabled}
                />
              </>
            </Tooltip>
          )
        };
      })
      .filter(Boolean) || [];

  return {
    actions,
    // isPermitted: actions.some((el) => !el.disabled)
    isPermitted: Boolean(actions.length),
    activeComponent
  };
};
